import InputField from "components/Common/InputField"
import ThemeModal from "components/Common/ThemeModal"
import { useMemo, useCallback } from "react"
import { toast } from "react-toastify"
import { Col, Row, Form, FormFeedback, Label, Button } from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import FormikCreatableField from "components/Common/FormikCreatableField"
import {
  useCreatePettyCashTransaction,
  useEditPettyCashTransaction,
} from "hooks/usePettyCashTransactions"
import VehicleIdSelect from "components/Common/VehicleIdSelect"
import { toLocalDatetimeInput } from "utils/formatDate"

const PettyCashTransactionAddEdit = ({
  modal,
  toggleModal,
  isEdit,
  transaction,
  optionLoading,
  paymentModesData,
  paymentForsData,
  paymentFromsData,
  receiversData,
  onSuccess,
}) => {
  const { mutateAsync: createTransaction, isPending: createLoading } =
    useCreatePettyCashTransaction()
  const { mutateAsync: editTransaction, isPending: editLoading } =
    useEditPettyCashTransaction()

  // Formik validation
  const validationSchema = Yup.object({
    payment_date: Yup.date().required("Please select payment date"),
    payment_type: Yup.string()
      .oneOf(["cashin", "cashout"])
      .required("Please select payment type"),
    amount: Yup.number()
      .required("Please enter amount")
      .min(1, "Amount must be >= 1"),
    payment_from_id: Yup.mixed().nullable(),
    payment_mode_id: Yup.mixed().nullable(),
    payment_for_id: Yup.mixed().nullable(),
    vehicle_id: Yup.mixed().nullable(),
    receiver_id: Yup.mixed().nullable(),
    remarks: Yup.string().nullable(),
  })

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      const payload = {
        ...values,
        payment_from_id:
          values.payment_from_id?.value || values.payment_from_id,
        payment_mode_id:
          values.payment_mode_id?.value || values.payment_mode_id,
        payment_for_id: values.payment_for_id?.value || values.payment_for_id,
        vehicle_id: values.vehicle_id?.value || values.vehicle_id,
        receiver_id: values.receiver_id?.value || values.receiver_id,
      }

      const action = isEdit
        ? editTransaction({ id: transaction.id, ...payload })
        : createTransaction(payload)

      try {
        const res = await action
        toast.success(
          res?.message ??
            `Transaction ${isEdit ? "updated" : "created"} successfully.`
        )
        resetForm()
        toggleModal()
        onSuccess?.()
      } catch (error) {
        console.error(error)
        toast.error("Failed to save transaction.")
      }
    },
    [isEdit, transaction, editTransaction, createTransaction, toggleModal, onSuccess]
  )

  const initialValues = useMemo(
    () => ({
      payment_date: isEdit
        ? transaction?.payment_date || toLocalDatetimeInput()
        : toLocalDatetimeInput(),
      payment_type: isEdit
        ? transaction?.payment_type || ""
        : transaction?.payment_type || "cashout",
      amount: transaction?.amount || "",
      payment_from_id: transaction?.paymentFrom
        ? {
            label: transaction.paymentFrom.name,
            value: transaction.paymentFrom.id,
          }
        : !isEdit
        ? "Petty Cash"
        : null,
      payment_mode_id: transaction?.paymentMode
        ? {
            label: transaction.paymentMode.name,
            value: transaction.paymentMode.id,
          }
        : !isEdit
        ? "Cash"
        : null,
      payment_for_id: transaction?.paymentFor
        ? {
            label: transaction.paymentFor.name,
            value: transaction.paymentFor.id,
          }
        : null,
      vehicle_id: transaction?.vehicle
        ? {
            label: `${transaction.vehicle.stock_id} - ${
              transaction.vehicle.full_chassis || ""
            }`,
            value: transaction.vehicle.id,
          }
        : null,
      receiver_id: transaction?.receiver
        ? { label: transaction.receiver.name, value: transaction.receiver.id }
        : null,
      remarks: transaction?.remarks || "",
    }),
    [transaction, isEdit]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  // Memoize button text
  const buttonText = useMemo(() => {
    const loading = editLoading || createLoading
    return `${isEdit ? "Save Changes" : "Add Transaction"}${
      loading ? "..." : ""
    }`
  }, [isEdit, editLoading, createLoading])

  const isSubmitDisabled = useMemo(
    () => optionLoading || editLoading || createLoading,
    [optionLoading, editLoading, createLoading]
  )

  return (
    <ThemeModal
      isOpen={modal}
      toggle={toggleModal}
      title={isEdit ? "Edit Transaction" : "Add Transaction"}
    >
      <Form
        onSubmit={e => {
          e.preventDefault()
          formik.handleSubmit()
        }}
      >
        <Row className="mb-3">
          <Col sm={6} className="mb-3">
            <Label htmlFor="payment_date">Payment Date</Label>
            <InputField
              type="datetime-local"
              name="payment_date"
              id="payment_date"
              value={formik.values.payment_date}
              onChange={formik.handleChange}
              invalid={
                formik.touched.payment_date && !!formik.errors.payment_date
              }
              loading={optionLoading}
            />
            {formik.touched.payment_date && formik.errors.payment_date && (
              <FormFeedback type="invalid">
                {formik.errors.payment_date}
              </FormFeedback>
            )}
          </Col>

          <Col sm={6} className="mb-3">
            <Label htmlFor="payment_type">Payment Type</Label>
            <InputField
              type="select"
              name="payment_type"
              id="payment_type"
              value={formik.values.payment_type}
              onChange={formik.handleChange}
              invalid={
                formik.touched.payment_type && !!formik.errors.payment_type
              }
              loading={optionLoading}
            >
              <option value="">Select Type</option>
              <option value="cashin">Cash In</option>
              <option value="cashout">Cash Out</option>
            </InputField>
            {formik.touched.payment_type && formik.errors.payment_type && (
              <FormFeedback type="invalid">
                {formik.errors.payment_type}
              </FormFeedback>
            )}
          </Col>

          <Col sm={6} className="mb-3">
            <Label htmlFor="amount">Amount</Label>
            <InputField
              type="number"
              name="amount"
              id="amount"
              value={formik.values.amount}
              onChange={formik.handleChange}
              invalid={formik.touched.amount && !!formik.errors.amount}
              loading={optionLoading}
            />
            {formik.touched.amount && formik.errors.amount && (
              <FormFeedback type="invalid">{formik.errors.amount}</FormFeedback>
            )}
          </Col>

          <Col sm={6} className="mb-3">
            <FormikCreatableField
              name="payment_from_id"
              label="Payment From"
              formik={formik}
              data={paymentFromsData}
              placeholder="Select Payment From"
            />
            {formik.touched.payment_from_id &&
              formik.errors.payment_from_id && (
                <FormFeedback type="invalid">
                  {formik.errors.payment_from_id}
                </FormFeedback>
              )}
          </Col>

          <Col sm={6} className="mb-3">
            <FormikCreatableField
              name="payment_mode_id"
              label="Payment Mode"
              formik={formik}
              data={paymentModesData}
              placeholder="Select Mode"
            />
            {formik.touched.payment_mode_id &&
              formik.errors.payment_mode_id && (
                <FormFeedback type="invalid">
                  {formik.errors.payment_mode_id}
                </FormFeedback>
              )}
          </Col>

          <Col sm={6} className="mb-3">
            <FormikCreatableField
              name="payment_for_id"
              label="Payment For"
              formik={formik}
              data={paymentForsData}
              placeholder="Select For"
            />
            {formik.touched.payment_for_id && formik.errors.payment_for_id && (
              <FormFeedback type="invalid">
                {formik.errors.payment_for_id}
              </FormFeedback>
            )}
          </Col>

          <Col sm={12} className="mb-3">
            <VehicleIdSelect formik={formik} />
          </Col>

          <Col sm={6} className="mb-3">
            <FormikCreatableField
              name="receiver_id"
              label="Receiver (Optional)"
              formik={formik}
              data={receiversData}
              placeholder="Select Receiver"
            />
            {formik.touched.receiver_id && formik.errors.receiver_id && (
              <FormFeedback type="invalid">
                {formik.errors.receiver_id}
              </FormFeedback>
            )}
          </Col>
        </Row>

        <div className="mb-3">
          <Label htmlFor="remarks">Remarks</Label>
          <InputField
            type="textarea"
            name="remarks"
            id="remarks"
            value={formik.values.remarks}
            onChange={formik.handleChange}
            invalid={formik.touched.remarks && !!formik.errors.remarks}
            loading={optionLoading}
          />
          {formik.touched.remarks && formik.errors.remarks && (
            <FormFeedback type="invalid">{formik.errors.remarks}</FormFeedback>
          )}
        </div>

        <div className="text-end">
          <Button color="success" type="submit" disabled={isSubmitDisabled}>
            {buttonText}
          </Button>
        </div>
      </Form>
    </ThemeModal>
  )
}

export default PettyCashTransactionAddEdit
